import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DynamicClass } from '@adornis/cms/db/dynamic-classes.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { ChancenDynamicClassProductFilter } from '../../products/entities/ChancenDynamicClassProductFilter.js';
import type { ChancenDynamicClassFormOfFinanzingFilter } from './ChancenDynamicClassFormOfFinanzingFilter.js';
import { FormOfFinancing } from './FormOfFinancing.js';

@Entity()
export class ChancenDynamicClassFormOfFinanzing extends DynamicClass {
  public static override _class = 'ChancenDynamicClassFormOfFinanzing';
  public static override _collectionName = 'dynamic-class-form-of-finanzing';

  @Field(() => String)
  public override collectionName: string = FormOfFinancing._collectionName;

  @Field(() => String)
  public override parentClassName: string = FormOfFinancing._class;

  @validate(nonOptional())
  @Field(() => String)
  public name!: string;

  @Mutation(() => Boolean)
  static checkDuplicateNameForDynamicFormOfFinanzing(
    @Arg('name', () => String) name: string,
    @Arg('id', () => String) id: string,
  ) {
    return async (gqlFields: void) => {
      const dynamicProductCollection = await getRawCollection<ChancenDynamicClassFormOfFinanzing>(this._collectionName);
      const duplicates = await dynamicProductCollection
        .find({ $and: [{ $or: [{ class: FormOfFinancing._class + name }, { name }] }, { _id: { $ne: id } }] })
        .toArray();
      return duplicates && duplicates.length;
    };
  }

  @Subscription(() => [ChancenDynamicClassFormOfFinanzing])
  static subscribeFormOfFinanzingByFilterPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => ChancenDynamicClassProductFilter) filter: ChancenDynamicClassFormOfFinanzingFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ChancenDynamicClassFormOfFinanzing>) => {
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<ChancenDynamicClassFormOfFinanzing>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { kuerzel: -1 },
          });
        }),
        catchError(err => {
          logger.error({ err }, 'Error during subscription');
          return of([] as ChancenDynamicClassFormOfFinanzing[]);
        }),
      );
    };
  }

  @Query(() => ChancenDynamicClassFormOfFinanzing)
  static getChancenDynamicClassFormOfFinanzingByClassName(@Arg('className', () => String) className: string) {
    return async (gqlFields: BaseQLSelectionSet<ChancenDynamicClassFormOfFinanzing>) => {
      const dynamicProductCollection = await getRawCollection<ChancenDynamicClassFormOfFinanzing>(this._collectionName);
      const result = await dynamicProductCollection.findOne({ class: className });

      return result;
    };
  }

  override toString() {
    return this.name;
  }

  public static generateClassNameFromName(name: string) {
    return (
      FormOfFinancing._class +
      name.replace(/ /, '').replace(/-/, '').replace(/_/, '').replace(/\(/, '').replace(/\)/, '')
    );
  }

  public generateClass(): Maybe<FormOfFinancing> {
    try {
      const nodeFromDynamicProduct = baseqlMetaData.entities.resolveNode(this.class);
      const classFromDynamicProduct = nodeFromDynamicProduct.type() as typeof FormOfFinancing;
      return new classFromDynamicProduct({});
    } catch (error) {
      console.error(error);
    }
    return null;
  }
}
